import {
  CategoryId,
  ChargeId,
  DealId,
  DealLineId,
  DealLineSkuId,
  DiscountId,
  OptionId,
  OptionListId,
  ProductId,
  SkuId,
  VariantId,
} from "../ids"
import { ICatalog, ICategory, ICharge, IDeal, IDiscount, IOptionList, IProduct, IVariant } from "../models"

export const updatedCatalog = (
  catalog: ICatalog,
  {
    variants = catalog.data.variants,
    products = catalog.data.products,
    categories = catalog.data.categories,
    optionLists = catalog.data.optionLists,
    deals = catalog.data.deals,
    discounts = catalog.data.discounts,
    charges = catalog.data.charges,
  }: {
    variants?: Array<IVariant>
    products?: Array<IProduct>
    categories?: Array<ICategory>
    optionLists?: Array<IOptionList>
    deals?: Array<IDeal>
    discounts?: Array<IDiscount>
    charges?: Array<ICharge>
  },
): ICatalog => {
  return {
    ...catalog,
    data: { ...catalog.data, variants, categories, products, optionLists, deals, discounts, charges },
  }
}

type RenumberFn = (id: number) => number

/**
 * Default renumbering function, which renumbers ids in the order they are passed.
 */
export const defaultRenumberFn = (): RenumberFn => {
  let maxId = -1
  const idMapping = new Map<number, number>() // old id -> new id

  return (id) => {
    if (idMapping.has(id)) {
      return idMapping.get(id)!
    } else {
      maxId++
      idMapping.set(id, maxId)
      return maxId
    }
  }
}

/**
 * Renumbers the ids in a predictable order, to make equivalent catalogs comparable.
 * @param catalog
 * @param renumberFn
 */
export const renumberCatalog = (catalog: ICatalog, renumberFn = defaultRenumberFn()): ICatalog => {
  // The highest id used in the catalog.
  let maxId = 0

  const renumber = (id: number) => {
    const newId = renumberFn(id)
    if (newId > maxId) {
      maxId = newId
    }
    return newId
  }

  return {
    ...catalog,
    idGenerator: () => ++maxId,
    data: {
      ...catalog.data,
      variants: catalog.data.variants.map((variant) => ({
        ...variant,
        id: VariantId(renumber(variant.id)),
      })),
      categories: catalog.data.categories.map((category) => ({
        ...category,
        id: CategoryId(renumber(category.id)),
        parentId: category.parentId === null ? null : CategoryId(renumber(category.parentId)),
      })),
      products: catalog.data.products.map((product) => ({
        ...product,
        id: ProductId(renumber(product.id)),
        categoryId: CategoryId(renumber(product.categoryId)),
        skus: product.skus.map((sku) => ({
          ...sku,
          id: SkuId(renumber(sku.id)),
          optionListIds: sku.optionListIds.map((optionListId) => OptionListId(renumber(optionListId))),
        })),
      })),
      optionLists: catalog.data.optionLists.map((optionList) => ({
        ...optionList,
        id: OptionListId(renumber(optionList.id)),
        options: optionList.options.map((option) => ({
          ...option,
          id: OptionId(renumber(option.id)),
        })),
      })),
      deals: catalog.data.deals.map((deal) => ({
        ...deal,
        id: DealId(renumber(deal.id)),
        categoryId: deal.categoryId === null ? null : CategoryId(renumber(deal.categoryId)),
        lines: deal.lines.map((line) => ({
          ...line,
          id: DealLineId(renumber(line.id)),
          skus: line.skus.map((sku) => ({
            ...sku,
            id: DealLineSkuId(renumber(sku.id)),
            skuId: SkuId(renumber(sku.skuId)),
          })),
        })),
      })),
      discounts: catalog.data.discounts.map((discount) => ({
        ...discount,
        id: DiscountId(renumber(discount.id)),
      })),
      charges: catalog.data.charges.map((charge) => ({
        ...charge,
        id: ChargeId(renumber(charge.id)),
      })),
    },
  }
}
